import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Card, CardContent, CardActionArea, Chip, Grid, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, LinearProgress, Stack,
} from '@mui/material';
import { Add, ChatBubbleOutline, FavoriteBorder, HomeWork } from '@mui/icons-material';
import listingService from '../services/listingService';
import { STATUS_COPY, STATUS_CHIP_COLOR } from '../components/listings/ListingStatusBanner';
import { brand, text, shadow } from '../theme/colors';

const EDGE = '#b9cbc7';
const LIVE_LIKE = ['published', 'unpublished', 'verified'];
const CLOSED = ['withdrawn', 'rejected', 'sold'];

// The dashboard's numbers link here with ?show=… (owner, 2026-09-28: "if I click
// on the 4 Live Listings it should take me there"). "In progress" matches the
// dashboard count in RealtorAttentionService. Interest filters use each
// listing's all-time counts; the dashboard shows the last 30 days.
const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'live', label: 'Live', test: (l) => l.status === 'published' },
  { key: 'in_progress', label: 'In progress', test: (l) => !LIVE_LIKE.includes(l.status) && !CLOSED.includes(l.status) },
  { key: 'enquiries', label: 'With enquiries', test: (l) => l.enquiry_count > 0 },
  { key: 'viewings', label: 'With viewing requests', test: (l) => l.viewing_count > 0 },
  { key: 'saved', label: 'Saved by buyers', test: (l) => l.saved_count > 0 },
];
const EMPTY_FILTER = {
  live: 'None of your listings is live right now.',
  in_progress: 'No listings in progress.',
  enquiries: 'No buyer has sent an enquiry yet.',
  viewings: 'No viewing requests yet.',
  saved: 'No buyer has saved one of your listings yet.',
};

const RealtorListingsPage = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [params, setParams] = useSearchParams();
  const show = FILTERS.some((f) => f.key === params.get('show')) ? params.get('show') : 'all';
  const pick = (key) => setParams(key === 'all' ? {} : { show: key }, { replace: true });
  // The dashboard's "List a property" lands here with ?new=1: open the form straight away.
  const [open, setOpen] = useState(params.get('new') === '1');
  const [form, setForm] = useState({ listingType: 'stand', tenureType: 'title_deed', title: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Closing always discards the draft so the next "New listing" starts clean.
  const closeDialog = () => { setOpen(false); setCreateError(''); setForm({ listingType: 'stand', tenureType: 'title_deed', title: '' }); };

  const load = useCallback(async () => {
    try { setLoading(true); setError(''); const r = await listingService.list(); setListings(r.data || []); }
    catch (e) { setError(e.response?.data?.error || 'Failed to load listings'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      setCreating(true); setCreateError('');
      const r = await listingService.create(form);
      navigate(`/realtor/listings/${r.data.id}`);
    } catch (e) { setCreateError(e.response?.data?.error || 'Could not create the listing'); }
    finally { setCreating(false); }
  };

  const visible = listings.filter(FILTERS.find((f) => f.key === show).test);

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} /></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary }}>My listings</Typography>
          <Typography variant="body2" sx={{ color: text.secondary }}>Every listing goes through a due-diligence dossier, Eqaya desk review and independent verification before buyers see it.</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)} sx={{ background: brand.gradient, fontWeight: 600, boxShadow: shadow.md, '&:hover': { background: brand.gradientHover } }}>
          New listing
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {!error && listings.length === 0 && (
        <Card variant="outlined" sx={{ borderColor: EDGE }}><CardContent sx={{ textAlign: 'center', py: 6 }}>
          <HomeWork sx={{ fontSize: 48, color: brand.primary, mb: 1 }} />
          <Typography sx={{ fontWeight: 600 }}>No listings yet</Typography>
          <Typography variant="body2" sx={{ color: text.secondary }}>Start with the property&apos;s title and type. You can add the dossier step by step.</Typography>
        </CardContent></Card>
      )}
      {listings.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }} role="tablist" aria-label="Filter listings">
          {FILTERS.map((f) => {
            const n = listings.filter(f.test).length;
            const on = show === f.key;
            return (
              <Chip key={f.key} role="tab" aria-selected={on} clickable onClick={() => pick(f.key)} label={`${f.label} · ${n}`}
                sx={{
                  fontWeight: 700, borderRadius: '8px', height: 34, border: `1px solid ${on ? brand.primaryDark : EDGE}`,
                  bgcolor: on ? brand.primaryDark : '#fff', color: on ? '#fff' : text.primary,
                  '&:hover': { bgcolor: on ? brand.primaryDark : '#f0f8f6' },
                }} />
            );
          })}
        </Stack>
      )}
      {listings.length > 0 && visible.length === 0 && (
        <Box sx={{ p: 3, borderRadius: 2, border: `1px dashed ${EDGE}`, bgcolor: '#fff', textAlign: 'center' }}>
          <Typography sx={{ color: text.secondary }}>{EMPTY_FILTER[show]}</Typography>
          <Button onClick={() => pick('all')} sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>Show all listings</Button>
        </Box>
      )}
      <Grid container spacing={2}>
        {visible.map((l) => {
          const pct = l.dossier.requiredCount ? Math.round((l.dossier.submittedCount / l.dossier.requiredCount) * 100) : 0;
          return (
            <Grid item xs={12} md={6} key={l.id}>
              <Card variant="outlined" sx={{
                height: '100%', bgcolor: '#fff', borderColor: EDGE, borderRadius: 2, boxShadow: '0 1px 2px rgba(19, 78, 74, 0.08)',
                transition: 'border-color 120ms', '&:hover': { borderColor: brand.primaryDark },
              }}>
                <CardActionArea onClick={() => navigate(`/realtor/listings/${l.id}`)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{l.title}</Typography>
                        <Typography variant="body2" sx={{ color: text.secondary, textTransform: 'capitalize' }}>
                          {l.listing_type}{l.location?.suburb ? ` · ${l.location.suburb}` : ''}{l.price_usd ? ` · USD ${Number(l.price_usd).toLocaleString()}` : ''}
                        </Typography>
                      </Box>
                      <Chip size="small" color={STATUS_CHIP_COLOR[l.status] || 'default'} label={(STATUS_COPY[l.status] || {}).title || l.status} />
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ color: text.secondary }}>Dossier: {l.dossier.submittedCount}/{l.dossier.requiredCount} items filled</Typography>
                      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                    </Box>
                    {/* Buyer interest. Both chips are hidden at zero: "0 saved" reads
                        as a verdict on the listing, while silence reads as "early days". */}
                    {(l.saved_count > 0 || l.viewing_count > 0 || l.enquiry_count > 0) && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                        {l.enquiry_count > 0 && (
                          <Chip size="small" variant="outlined" icon={<ChatBubbleOutline />} label={`${l.enquiry_count} enquir${l.enquiry_count === 1 ? 'y' : 'ies'}`} />
                        )}
                        {l.saved_count > 0 && (
                          <Chip size="small" variant="outlined" icon={<FavoriteBorder />} label={`${l.saved_count} saved`} />
                        )}
                        {l.viewing_count > 0 && (
                          <Chip size="small" variant="outlined" color="primary" label={`${l.viewing_count} viewing request${l.viewing_count === 1 ? '' : 's'}`} />
                        )}
                      </Stack>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={open} onClose={() => !creating && closeDialog()} fullWidth maxWidth="sm">
        <DialogTitle>New listing</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setCreateError('')}>{createError}</Alert>}
          <TextField select fullWidth label="Property type" value={form.listingType} sx={{ mt: 1, mb: 2 }} onChange={(e) => setForm({ ...form, listingType: e.target.value })}>
            <MenuItem value="stand">Stand (vacant land)</MenuItem>
            <MenuItem value="house">House</MenuItem>
          </TextField>
          <TextField select fullWidth label="How does the seller hold it?" value={form.tenureType} sx={{ mb: 2 }} onChange={(e) => setForm({ ...form, tenureType: e.target.value })}
            helperText={form.tenureType === 'cession'
              ? 'No title deed yet: the rights come from a council or a land developer. Eqaya checks the cession at their office instead of the Deeds Office.'
              : 'A title deed registered at the Deeds Office.'}>
            <MenuItem value="title_deed">Title deed</MenuItem>
            <MenuItem value="cession">Cession (no title deed yet)</MenuItem>
          </TextField>
          <TextField autoFocus fullWidth label="Listing title" placeholder="e.g. 2000 m² stand, Crowhill, Borrowdale" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} inputProps={{ maxLength: 160 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={creating || form.title.trim().length < 3}>Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RealtorListingsPage;
